import "./index.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Fragment, type ReactNode, StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { ApiError, followLanguage, LICENCE_READ_ONLY, sessionRefused } from "./api/client";
import { App } from "./app/App";
import { loadEnvironment } from "./app/environment";
import { PreprodFrame } from "./app/PreprodStrip";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { installPressFeedback } from "./components/pressFeedback";
import { installErrorLog } from "./feedback/errorLog";
import { initialLang, loadLang, useLang } from "./i18n/i18n";
import { LicenceGate } from "./licence/LicenceGate";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      // Un refus de licence (licence absente, ou expirée en lecture seule) ou de session (verrouillée, fermée,
      // expirée, autre compte : docs/17 §4.8) n'est pas une panne passagère : pas de nouvel essai.
      retry: (failures, error) =>
        !(error instanceof ApiError && (error.code === "LICENCE_REQUIRED" || error.code === LICENCE_READ_ONLY)) &&
        !sessionRefused(error) &&
        failures < 1,
    },
  },
});

/** Documents du dossier dans la langue de l'interface (docs/13) : au changement de langue, toutes les requêtes sont
 *  relues (en-tête `X-Generex-Langue` et cookie déjà dans la nouvelle langue) ; l'ordre, la mise en ambiance, la
 *  fiche pays… ouverts se rechargent d'eux-mêmes, traductions en cours comprises. */
followLanguage(queryClient);

/** Changer de langue remonte l'application : chaque texte est relu dans la nouvelle langue. */
function Localized({ children }: { children: ReactNode }) {
  return <Fragment key={useLang()}>{children}</Fragment>;
}

// Effet de pression de tous les boutons, à l'appui (zoom avant puis retour).
installPressFeedback();
// Dernières erreurs JavaScript, jointes au contexte d'une anomalie signalée (registre F39).
installErrorLog();

// Dictionnaire de la langue du poste chargé avant le premier affichage (l'anglais seulement s'il est choisi), avec
// l'environnement servi (préproduction : bandeau et bouton « Promouvoir », registre F35 ; 1,5 s au plus).
void Promise.allSettled([loadLang(initialLang()), loadEnvironment()]).finally(() =>
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <Localized>
          <ErrorBoundary>
            {/* Bandeau « PRÉPRODUCTION » au-dessus de tous les écrans, en préproduction seulement. */}
            <PreprodFrame>
              <LicenceGate>
                <App />
              </LicenceGate>
            </PreprodFrame>
          </ErrorBoundary>
        </Localized>
      </QueryClientProvider>
    </StrictMode>,
  ),
);
